import { isLocalMediaPath } from "~utils/url"

type ThumbnailSource = string | { uri: string }

function normalizeUri(source: ThumbnailSource): string {
  if (typeof source === "string") return source
  return source?.uri ?? ""
}

function captureFrame(uri: string, timeMs: number, quality: number) {
  return new Promise<{ uri: string }>((resolve) => {
    const video = document.createElement("video")
    video.muted = true
    video.playsInline = true
    video.preload = "auto"
    video.src = uri

    let settled = false
    const fail = () => {
      if (settled) return
      settled = true
      resolve({ uri: "" })
    }

    const timeout = window.setTimeout(fail, 12_000)

    video.onerror = fail
    video.onloadeddata = () => {
      const seekTo =
        timeMs > 0 && Number.isFinite(video.duration)
          ? Math.min(timeMs / 1000, Math.max(0, video.duration - 0.05))
          : 0
      video.currentTime = seekTo
    }
    video.onseeked = () => {
      if (settled) return
      try {
        const canvas = document.createElement("canvas")
        canvas.width = video.videoWidth || 320
        canvas.height = video.videoHeight || 240
        const ctx = canvas.getContext("2d")
        if (!ctx) {
          fail()
          return
        }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        canvas.toBlob(
          (blob) => {
            window.clearTimeout(timeout)
            if (settled) return
            settled = true
            if (!blob) {
              resolve({ uri: "" })
              return
            }
            resolve({ uri: URL.createObjectURL(blob) })
          },
          "image/jpeg",
          quality,
        )
      } catch {
        fail()
      }
    }
  })
}

/**
 * 从视频 URL 截取首帧缩略图（浏览器 canvas）。
 * 仅对 blob/data/file 等本地资源截帧；远程 CDN 无 CORS 时不设 crossOrigin，避免被浏览器拦截。
 */
export async function getThumbnailAsync(
  source: ThumbnailSource,
  options?: { time?: number; quality?: number },
) {
  const uri = normalizeUri(source)
  if (!uri || typeof document === "undefined") return { uri: "" }
  if (!isLocalMediaPath(uri)) return { uri: "" }

  const time = options?.time ?? 100
  const quality = options?.quality ?? 0.8
  return captureFrame(uri, time, quality)
}

export default { getThumbnailAsync }
